<script lang="ts" setup>
import type { ICustomFooterLinks } from "./footer.interface"
import { computed, useSlots } from "vue"
import { LanguageSelector } from "@/components"
import { useTranslation } from "@/composables"
import { getBrandFooterUrls } from "./footer.constants"
import FooterBrand from "./FooterBrand.vue"
import FooterLinks from "./FooterLinks.vue"
import FooterSocial from "./FooterSocial.vue"

interface FooterProps {
  customLogo?: string
  customFooterLinks?: ICustomFooterLinks
  hideLocaleSwitch?: boolean
  isFreemiumEvent?: boolean
  extendBottomPadding?: boolean
}

const props = withDefaults(defineProps<FooterProps>(), {
  hideLocaleSwitch: false,
  customLogo: undefined,
  customFooterLinks: undefined,
  isFreemiumEvent: false,
  extendBottomPadding: false,
})

const { t } = useTranslation()

const slots = useSlots()

const hasCustomLogo = computed(() => Boolean(props.customLogo))
const hasDefaultContent = computed(() => Boolean(slots.default))
const hasCustomLinks = computed(() => props.customFooterLinks?.footerLinks && props.customFooterLinks.footerLinks.length > 0)
const hasCustomSocial = computed(
  () =>
    props.customFooterLinks?.socialLinks
    && Object.values(props.customFooterLinks.socialLinks).some(link => !!link),
)
const hideGpxBrand = computed(() => hasCustomLogo.value || hasCustomLinks.value || hasCustomSocial.value || hasDefaultContent.value)

const urls = getBrandFooterUrls()
</script>

<template>
  <footer
    id="footer"
    class="gpx-footer flex-col"
    :class="{ 'pb-10': extendBottomPadding }"
  >
    <div class="container gpx-footer-container">
      <div class="gpx-brand-section">
        <FooterBrand :custom-logo="customLogo" :hide-gpx-brand="hideGpxBrand" :is-freemium-event="isFreemiumEvent" />
        <FooterSocial
          v-if="!hideGpxBrand || hasCustomSocial"
          is-template
          :custom-social="hasCustomSocial ? customFooterLinks?.socialLinks : undefined"
        />
        <LanguageSelector v-if="!hideGpxBrand && !hideLocaleSwitch" is-template />
      </div>
      <div class="gpx-links-section">
        <slot name="default">
          <FooterLinks :custom-links="hasCustomLinks ? customFooterLinks?.footerLinks : undefined" />
        </slot>
      </div>
    </div>
    <div v-if="hideGpxBrand" class="gpx-footer-bottom">
      <FooterBrand :is-freemium-event="isFreemiumEvent" />
      <ul v-if="hasDefaultContent || hasCustomLinks">
        <li>
          <a :href="urls.termsOfService" target="_blank">
            {{ t("footer.terms_of_service") }}
          </a>
        </li>
        <li>|</li>
        <li>
          <a :href="urls.privacy" target="_blank">{{ t("footer.privacy_policy") }}</a>
        </li>
        <li>|</li>
        <li>
          <a :href="urls.acceptableUsePolicy" target="_blank">
            {{ t("footer.acceptable_use_policy") }}
          </a>
        </li>
      </ul>
      <FooterSocial v-if="!hasCustomSocial" />
      <LanguageSelector v-if="!hideLocaleSwitch" />
    </div>
  </footer>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css";

.gpx-footer {
  @apply bg-footer-bg flex;
}

.gpx-footer-container {
  @apply w-full flex justify-between gap-8 py-10;
}

.gpx-footer-bottom {
  @apply flex flex-col items-center w-full bg-white py-10;
}

.gpx-footer-bottom ul {
  margin: 1rem 0 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.gpx-footer-bottom ul a {
  @apply text-primary text-sm;
}

@media (width <= 768px) {
  .gpx-footer-container {
    flex-direction: column-reverse;
  }

  .gpx-brand-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 2.5rem;
  }

  .gpx-links-section {
    align-self: center;
    text-align: center;
  }
}
</style>
